import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, Download, Gauge, Info, Search, Medal as MedalIcon } from "lucide-react";
import { Pagination, Select, Tabs } from "./ui";
import { ResultRow } from "../data/types";
import { downloadCsv } from "../utils/download";
import { usePortalLink } from "../utils/portalLink";
import { formatScore } from "../utils/score";
import { formatVelocity, formatVelocitySpread, useUnits } from "../utils/units";

/*
 * Results table with working scope tabs (per the proposal's Results module):
 *  - Overall: single leaderboard
 *  - Class / Grade / Club: rows regrouped and re-ranked within each group
 */
export default function ResultsBlock({ rows, showHits, scoreLabel = "Score", exportName = "results", stringLinkPrefix }: {
  rows: ResultRow[];
  showHits?: boolean;
  scoreLabel?: string;
  exportName?: string;
  /** e.g. "STR-vm600" — adds a per-row link to the shooter's bullet-impact target map */
  stringLinkPrefix?: string;
}) {
  const link = usePortalLink();
  const { units } = useUnits();
  const [tab, setTab] = useState("Overall Results");
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("All Classes");
  const [gradeFilter, setGradeFilter] = useState("All Grades");
  const [clubFilter, setClubFilter] = useState("All Clubs");
  /*
   * Velocity columns are shown whenever the targets recorded velocities — that
   * is what shooters come to the results for after the score itself. They can be
   * folded away because five extra columns widen the table considerably.
   */
  const hasVelocity = useMemo(() => rows.some((r) => r.velocity && r.velocity.count > 0), [rows]);
  const [showVelocity, setShowVelocity] = useState(true);
  const velocityOn = hasVelocity && showVelocity;

  const clubs = useMemo(() => Array.from(new Set(rows.map((r) => r.club))).sort(), [rows]);
  // Classes come from the data — a fullbore shoot fields TR/FTR/FO/SH, a precision
  // meeting Open/Standard, and an imported shoot may field something else again.
  const classes = useMemo(() => Array.from(new Set(rows.map((r) => r.rifleClass).filter(Boolean))).sort(), [rows]);
  // Grades likewise. Fullbore grades A/B/C, other disciplines grade by name, and
  // whole classes are shot ungraded — so the filter offers what the results
  // actually contain, and disappears when nothing here is graded at all.
  const grades = useMemo(() => Array.from(new Set(rows.map((r) => r.grade).filter(Boolean))).sort(), [rows]);
  // TR scores centres as V, F-class as X — follow whatever the results carry.
  const centreHeader = useMemo(() => {
    const labels = Array.from(new Set(rows.map((r) => r.centreLabel).filter(Boolean)));
    return labels.length === 1 ? `${labels[0]} Count` : "V / X Count";
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q || r.shooter.toLowerCase().includes(q) || r.sid.includes(q)) &&
        (classFilter === "All Classes" || r.rifleClass === classFilter) &&
        (gradeFilter === "All Grades" || r.grade === gradeFilter) &&
        (clubFilter === "All Clubs" || r.club === clubFilter)
    );
  }, [rows, search, classFilter, gradeFilter, clubFilter]);

  /*
   * The combined view is a LIST, not a ladder.
   *
   * Classes do not compete with one another — Target Rifle counts five points a
   * shot, F-Class six, ISSF ten — so a single ranked table down the middle of
   * them ranks by which class scores most easily. Ranks belong on the class
   * boards, where they mean something; here the order is alphabetical so a
   * competitor can find their own name.
   *
   * By surname, as results have always been printed: the name arrives as one
   * field, so the last word is taken as the surname, which is right for
   * "David Waters" and for "Thanjon Michniewicz" alike.
   */
  const surnameOf = (name: string) => {
    const parts = String(name || "").trim().split(/s+/);
    return (parts[parts.length - 1] || "").toLowerCase();
  };
  const alphabetical = useMemo(
    () =>
      [...filtered].sort(
        (a, b) =>
          surnameOf(a.shooter).localeCompare(surnameOf(b.shooter)) ||
          String(a.shooter).localeCompare(String(b.shooter))
      ),
    [filtered]
  );

  // Group + re-rank within each group for Class/Grade/Club tabs
  const groups = useMemo(() => {
    const keyOf =
      tab === "Class Results" ? (r: ResultRow) => r.rifleClass
      : tab === "Grade Results" ? (r: ResultRow) => (r.grade ? `Grade ${r.grade}` : "Ungraded")
      : tab === "Club Results" ? (r: ResultRow) => r.club
      : null;
    if (!keyOf) return null;
    const map = new Map<string, ResultRow[]>();
    filtered.forEach((r) => {
      const k = keyOf(r);
      map.set(k, [...(map.get(k) || []), r]);
    });
    return Array.from(map.entries()).map(([name, members]) => ({
      name,
      members: [...members].sort((a, b) => b.score - a.score || b.xCount - a.xCount),
    }));
  }, [filtered, tab]);

  /*
   * Every competitor on one page. A results table is scanned for a name or
   * checked against a score sheet, and splitting fifty shooters across five
   * pages made both harder; the card scrolls instead, with the heading and
   * filters staying put.
   */
  // The combined table is not a ladder, so it reads alphabetically.
  const paged = alphabetical;

  const exportCsv = () => {
    const headers = [
      "Rank", "Class Rank", "Shooter", "SID", "Club", "Class", "Grade", scoreLabel, "Possible", centreHeader,
      ...(showHits ? ["Hits"] : []),
      // Velocity always exports when it was recorded, whether or not the columns
      // are folded away on screen — the export is the working copy.
      ...(hasVelocity ? ["Velocity Avg", "Velocity SD", "Velocity ES", "Velocity High", "Velocity Low"] : []),
    ];
    downloadCsv(
      `${exportName}.csv`,
      headers,
      filtered.map((r) => [
        r.rank,
        r.classRank ?? "",
        r.shooter,
        r.sid,
        r.club,
        r.rifleClass,
        r.grade,
        formatScore(r.score),
        r.possible ?? "",
        r.xCount,
        ...(showHits ? [r.hits ?? ""] : []),
        ...(hasVelocity
          ? [r.velocity?.avg ?? "", r.velocity?.sd ?? "", r.velocity?.es ?? "", r.velocity?.high ?? "", r.velocity?.low ?? ""]
          : []),
      ])
    );
  };

  const headerCells = (ranked = true) => (
    <tr className="border-b border-line">
      {ranked && <th className="th">Rank</th>}
      <th className="th">Shooter</th>
      <th className="th">Club</th>
      <th className="th">Class</th>
      <th className="th">Grade</th>
      <th className="th text-right">{scoreLabel}</th>
      <th className="th text-right">{centreHeader}</th>
      {showHits && <th className="th text-right">Hits</th>}
      {velocityOn && (
        <>
          <th className="th text-right" title="Average muzzle velocity">Avg</th>
          <th className="th text-right" title="Standard deviation of muzzle velocity">SD</th>
          <th className="th text-right" title="Extreme spread — fastest minus slowest">ES</th>
          <th className="th text-right" title="Fastest shot">High</th>
          <th className="th text-right" title="Slowest shot">Low</th>
        </>
      )}
      {stringLinkPrefix && <th className="th text-center">Impacts</th>}
    </tr>
  );

  const velocityCells = (r: ResultRow) =>
    velocityOn ? (
      r.velocity && r.velocity.count > 0 ? (
        <>
          <td className="td text-right font-semibold">{formatVelocity(r.velocity.avg, units.velocity)}</td>
          <td className="td text-right">{formatVelocitySpread(r.velocity.sd, units.velocity)}</td>
          <td className="td text-right">{formatVelocitySpread(r.velocity.es, units.velocity)}</td>
          <td className="td text-right text-muted">{formatVelocity(r.velocity.high, units.velocity)}</td>
          <td className="td text-right text-muted">{formatVelocity(r.velocity.low, units.velocity)}</td>
        </>
      ) : (
        <td className="td text-center text-muted" colSpan={5}>
          —
        </td>
      )
    ) : null;

  /*
   * Gold, silver and bronze for the first three of a class board — the way a
   * prize list is read. `rank` of 0 means this table is not ranked at all.
   */
  const medal = ["text-[#B8860B]", "text-[#7D8794]", "text-[#A0522D]"];
  const renderRow = (r: ResultRow, rank: number, i: number) => (
    <tr key={`${r.sid}-${rank || i}`} onClick={() => window.open(link(`/shooters/${r.sid}`), "_blank", "noopener")} className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
      {rank > 0 && (
        <td className={`td font-bold ${rank <= 3 ? medal[rank - 1] : ""}`}>
          {rank <= 3 ? <MedalIcon size={14} className={`mr-1 inline ${medal[rank - 1]}`} /> : null}
          {rank}
        </td>
      )}
      <td className="td">
        <Link to={link(`/shooters/${r.sid}`)} target="_blank" rel="noopener noreferrer" className="font-bold text-ink hover:text-primary">
          {r.shooter}
        </Link>
        <div className="text-xs text-muted">SID: {r.sid}</div>
      </td>
      <td className="td text-slate-600">{r.club}</td>
      <td className="td">{r.rifleClass}</td>
      <td className="td">{r.grade}</td>
      <td className="td text-right font-bold">
        {formatScore(r.score)}
        {r.possible ? <span className="ml-1 text-xs font-semibold text-muted">/ {r.possible}</span> : null}
      </td>
      <td className="td text-right font-semibold">{r.xCount}</td>
      {showHits && <td className="td text-right">{r.hits}</td>}
      {velocityCells(r)}
      {stringLinkPrefix && (
        <td className="td text-center">
          {/* Imported results carry their own string ids; seeded rows fall back to the prefix convention. */}
          <span className="inline-flex justify-center gap-1">
            {(r.stringIds?.length ? r.stringIds : [`${stringLinkPrefix}-${r.sid}-1`]).map((sid) => (
              <Link
                key={sid}
                onClick={(e) => e.stopPropagation()}
                to={link(`/strings/${sid}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-line text-primary transition-colors hover:bg-primary-soft"
                title="View bullet impacts on the target"
              >
                <Crosshair size={13} />
              </Link>
            ))}
          </span>
        </td>
      )}
    </tr>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-56">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Search shooter or SID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
        <Select options={["All Classes", ...classes]} value={classFilter} onChange={setClassFilter} className="w-full sm:w-36" />
        {grades.length > 0 && (
          <Select options={["All Grades", ...grades]} value={gradeFilter} onChange={setGradeFilter} className="w-full sm:w-32" />
        )}
        <Select options={["All Clubs", ...clubs]} value={clubFilter} onChange={setClubFilter} className="w-full sm:w-44" />
      </div>

      {/*
        Tabs own the row; the export action sits apart on the right so neither
        squeezes the other. On narrow screens the button drops below the tabs
        instead of truncating them.
      */}
      <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <Tabs
          tabs={["Overall Results", "Class Results", "Grade Results", "Club Results"]}
          active={tab}
          onChange={(t) => {
            setTab(t);
          }}
          className="min-w-0 lg:flex-1"
        />
        <div className="flex shrink-0 items-center gap-2 self-start lg:self-auto">
          {hasVelocity && (
            <button
              onClick={() => setShowVelocity((v) => !v)}
              className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3.5 text-[13px] font-semibold transition-colors ${
                showVelocity ? "border-primary bg-primary-soft text-primary" : "border-line bg-white text-ink hover:bg-slate-50"
              }`}
              title="Show or hide muzzle velocity columns"
            >
              <Gauge size={14} /> Velocity ({units.velocity})
            </button>
          )}
          <button
            onClick={exportCsv}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {groups ? (
        /* Grouped tabs: one ranked table per class / grade / club */
        <div className="mt-4 space-y-5">
          {groups.map((g) => (
            <div key={g.name} className="card">
              <div className="flex items-center justify-between border-b border-line bg-slate-50 px-4 py-3">
                <h4 className="text-sm font-extrabold text-ink">{g.name}</h4>
                <span className="text-xs font-semibold text-muted">{g.members.length} shooters</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>{headerCells(true)}</thead>
                  <tbody className="divide-y divide-line">{g.members.map((r, i) => renderRow(r, i + 1, i))}</tbody>
                </table>
              </div>
            </div>
          ))}
          {groups.length === 0 && (
            <div className="card p-10 text-center text-sm text-muted">No results match the current filters.</div>
          )}
        </div>
      ) : (
        <div className="card mt-3">
          <div className="table-scroll [--table-offset:470px]">
            <table className="w-full min-w-[760px]">
            <thead className="bg-slate-50">{headerCells(false)}</thead>
            <tbody className="divide-y divide-line">{paged.map((r, i) => renderRow(r, 0, i))}</tbody>
            </table>
          </div>
          {filtered.length === 0 && <div className="p-10 text-center text-sm text-muted">No results match the current filters.</div>}
          <div className="border-t border-line">
            <Pagination
              summary={
                filtered.length === rows.length
                  ? `${filtered.length} ${filtered.length === 1 ? "competitor" : "competitors"}`
                  : `${filtered.length} of ${rows.length} competitors match the filters`
              }
            />
          </div>
        </div>
      )}

      {/* One <span> so the sentence wraps as prose — as separate flex children it
          broke into fragments with gaps between them on narrow screens. */}
      <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-primary-soft px-4 py-3 text-[13px] text-slate-600">
        <Info size={15} className="mt-0.5 shrink-0 text-primary" />
        <span>
          Scores are shown as <b className="text-primary">total · centres</b> (e.g. 49.4V). Ties are broken by{" "}
          <b className="text-primary">{centreHeader}</b>, then hits.
        </span>
      </div>
    </div>
  );
}
